import { tryOnScopeDispose, useIdle, useStorage, watchThrottled } from '@vueuse/core';
import { ref, watch } from 'vue';
import type { Ref } from 'vue';

const SHARED_LAST_ACTIVE_KEY = 'stuut:idle:last-active';

// Don't hammer localStorage on every mousemove/keystroke — publishing once
// every few seconds is plenty given the timeout is measured in minutes.
const PUBLISH_THROTTLE_MS = 5_000;

export interface UseSharedIdleReturn {
  /** Drop the shared timestamp, e.g. before an idle-triggered sign-out. */
  clear: () => void;
  /** True only when every open tab has been idle for longer than `timeout`. */
  idle: Ref<boolean>;
  /** The newest activity timestamp seen across all tabs (epoch ms). */
  lastActive: Ref<number>;
}

/**
 * Cross-tab idle detection.
 *
 * Plain `useIdle` arms an independent timer per document, so an untouched tab
 * reports the user as idle even while they're busy in another tab. This shares
 * a single "last activity" timestamp across every tab via `localStorage` (whose
 * writes fire `storage` events in sibling tabs) and only reports idle once the
 * shared timestamp is older than `timeout` — i.e. all tabs have gone quiet.
 *
 * @param timeout Idle threshold in milliseconds.
 */
export function useSharedIdle(timeout: number): UseSharedIdleReturn {
  // Per-tab activity detection. `lastActive` bumps to `now` on each user event.
  const { lastActive } = useIdle(timeout);

  // Shared across tabs. `useStorage` reads any existing value on init (so a
  // freshly opened tab inherits a sibling's recent activity) and stays reactive
  // to `storage` events written by other tabs.
  const sharedLastActive = useStorage(SHARED_LAST_ACTIVE_KEY, Date.now());

  const idle = ref(false);

  // Publish this tab's activity to the shared timestamp. Throttled (not
  // debounced) so sustained activity keeps publishing — a debounce would only
  // fire once activity stops, letting a sibling tab time out mid-use. The
  // trailing edge captures the final timestamp before the user goes quiet. We
  // compare against the shared value so we only ever push it forward.
  watchThrottled(
    lastActive,
    (value) => {
      if (value > sharedLastActive.value) {
        sharedLastActive.value = value;
      }
    },
    { throttle: PUBLISH_THROTTLE_MS, immediate: true },
  );

  // Re-arm an idle timer against the shared timestamp. Reschedules whenever the
  // shared value moves — either from this tab's activity or a sibling's
  // `storage` event — so idle only fires once the most recent activity across
  // all tabs is older than `timeout`.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedule = () => {
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }

    const remaining = sharedLastActive.value + timeout - Date.now();
    if (remaining <= 0) {
      idle.value = true;
      return;
    }

    idle.value = false;
    timer = setTimeout(() => {
      idle.value = true;
    }, remaining);
  };

  watch(sharedLastActive, schedule, { immediate: true });

  tryOnScopeDispose(() => {
    if (timer) clearTimeout(timer);
  });

  // Drop the shared timestamp so a stale value can't trip an immediate idle
  // logout on the next login. (Belt-and-suspenders: mount-time publishing and
  // the non-immediate idle watcher already guard against this.) Assigning null
  // goes through useStorage's RemovableRef, which removes the key for us.
  const clear = () => {
    sharedLastActive.value = null;
  };

  return { idle, lastActive: sharedLastActive, clear };
}
